import { effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  AppError,
  SpinnerService,
  ToastService,
  ToastType,
  WINDOW,
} from '@hglobal/fin-ui';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, switchMap } from 'rxjs';

import { AuthSyncService } from '@iw/core/services';
import {
  RouterStore,
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StoreHelperService,
  withRequestHandler,
  withRequestStatus,
} from '@iw/store';
import { UserStore } from '@iw/users';

import { AUTH_API } from './api';
import { AuthState } from './models';
import {
  SigninDialog,
  TfaCodesComponent,
  TfaEnableComponent,
} from '../../auth/signin/components';

const initialState: AuthState = {
  isAuthorized: false,
  is2faEnabled: false,
  recoveryCodes: [],
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withDevtools('auth'),
  withState(initialState),
  withRequestStatus([
    'signIn',
    'signOut',
    'enable2fa',
    'remove2fa',
    'verifyCode',
    'resetCode',
    'logout',
  ]),
  withRequestHandler(),
  withProps((store) => ({
    store,
    authApi: inject(AUTH_API),
    routerStore: inject(RouterStore),
    toast: inject(ToastService),
    dialog: inject(SigninDialog),
    window: inject<Window>(WINDOW),
    spinnerService: inject(SpinnerService),
    storeHelperService: inject(StoreHelperService),
    authSyncService: inject(AuthSyncService),
    userStore: inject(UserStore),
  })),
  withMethods(({ store }) => ({
    setAuthStatus: (state: boolean) => {
      patchState(store, { isAuthorized: state });
    },
    setTfaStatus: (state: boolean) => {
      patchState(store, { is2faEnabled: state });
    },
  })),
  withMethods(({ window, routerStore, setAuthStatus }) => ({
    navigateAfter: () => {
      const params = new URLSearchParams(window.location.search);
      const encodedUrl = params.get('url');
      let url = encodedUrl ? decodeURIComponent(encodedUrl) : null;

      url =
        url && !url.includes('signin')
          ? url[0] === '/'
            ? url
            : '/' + url
          : '/';

      routerStore.navigateByUrl(url);
    },
    clearAuthState: () => {
      const { pathname } = window.location;
      const path = pathname.includes('signin') ? '' : pathname;

      const route = ['/signin'];
      const extras = {
        queryParams: {
          url: !path || path === '/' ? null : encodeURIComponent(path),
        },
      };
      routerStore.navigate(route, extras);
      setAuthStatus(false);
    },
  })),
  withMethods(
    ({
      store,
      authApi,
      dialog,
      toast,
      navigateAfter,
      authSyncService,
      setAuthStatus,
      userStore,
    }) => ({
      enable2fa: rxMethod<void>(
        switchMap(() => {
          store.resetRequestStatus('verifyCode');
          patchState(store, setPendingStatusFor('enable2fa'));
          return authApi.enable2fa().pipe(
            tapResponse({
              next: (payload) => {
                patchState(
                  store,
                  { recoveryCodes: payload.recoveryCodes },
                  setSuccessStatusFor('enable2fa'),
                );
                const payloadData = payload || {};
                dialog.open(TfaEnableComponent, { ...payloadData });
              },
              error: (error: AppError) => {
                patchState(store, setErrorStatusFor('enable2fa', error));
                toast.show({ type: ToastType.Error, message: error.message });
              },
            }),
          );
        }),
      ),
      remove2fa: rxMethod<void>(
        switchMap(() => {
          patchState(store, setPendingStatusFor('remove2fa'));
          return authApi.remove2fa().pipe(
            tapResponse({
              next: () => patchState(store, setSuccessStatusFor('remove2fa')),
              error: (error: AppError) => {
                patchState(store, setErrorStatusFor('remove2fa', error));
                toast.show({ type: ToastType.Error, message: error.message });
              },
            }),
          );
        }),
      ),
      resetCode: rxMethod<{ recoveryCode: string }>(
        switchMap(({ recoveryCode }) => {
          patchState(store, setPendingStatusFor('resetCode'));
          return authApi.resetCode(recoveryCode).pipe(
            tapResponse({
              next: () => {
                patchState(store, setSuccessStatusFor('resetCode'));
                setAuthStatus(true);
                userStore.getMe();
                authSyncService.notifyLogin();
                navigateAfter();
              },
              error: (error: AppError) => {
                patchState(store, setErrorStatusFor('resetCode', error));
                if (error.code === 'throttled') {
                  toast.show({
                    type: ToastType.Error,
                    message: `error.${error.code}`,
                  });
                }
              },
            }),
          );
        }),
      ),
      verifyCode: rxMethod<{ code: string }>(
        switchMap(({ code }) => {
          patchState(store, setPendingStatusFor('verifyCode'));
          return authApi.verifyCode(code).pipe(
            tapResponse({
              next: () => {
                patchState(store, setSuccessStatusFor('verifyCode'));
                setAuthStatus(true);
                userStore.getMe();
                authSyncService.notifyLogin();
                if (store.is2faEnabled()) {
                  navigateAfter();
                } else {
                  dialog.open(TfaCodesComponent);
                }
              },
              error: (error: AppError) => {
                patchState(store, setErrorStatusFor('verifyCode', error));
                if (error.code === 'throttled') {
                  toast.show({
                    type: ToastType.Error,
                    message: `error.${error.code}`,
                  });
                }
              },
            }),
          );
        }),
      ),
    }),
  ),
  withMethods(({ store, authApi, toast, clearAuthState, authSyncService }) => ({
    logout: rxMethod<void>(
      switchMap(() => {
        if (store.isLogoutPending()) {
          return EMPTY;
        }

        patchState(store, setPendingStatusFor('logout'));
        return authApi.logout().pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('logout'));
              authSyncService.notifyLogout();
              clearAuthState();
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('logout', error));
              toast.show({ type: ToastType.Error, message: error.message });
            },
          }),
        );
      }),
    ),
  })),
  withHooks({
    onInit(store) {
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        if (store.error()) {
          untracked(() => store.storeHelperService.handleError(store.error()));
        }
      });
    },
  }),
);
